import React, { useEffect, useState } from 'react'
import {
  Chip,
  CircularProgress,
  FormControl,
  makeStyles,
  Menu,
  MenuItem,
  Paper,
  Select,
  Typography,
  IconButton,
  DialogContent,
  Dialog,
  Button
} from '@material-ui/core'
import {
  ContentCopy,
  Delete,
  Download,
  Edit,
  MoreVert as MoreVertIcon,
  RestartAlt,
  Warning
} from '@material-ui/icons'
import { Box } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import speakerSlice, { getSpeakers } from 'store/speaker'
import { caseInsensitiveSort } from 'utils'
import pollingSlice, {
  deletePollById,
  fetchPollQuestions,
  getConferenceBasedPolls,
  resetPollById
} from 'store/polling'
import BasicTable from 'components/BasicTable'
import ResourceNotFound from 'components/ResourceNotFound'
import Wrapper from 'components/Wrapper'
import getTableOptions from 'utils/getTableOptions'
import { getPollStatusText, getPollType } from 'utils/polling'
import { downloadPollResultsInCSV } from 'utils/downloadCSV'
import PollingForm from 'views/admin/subjects/PollingForm/PollingForm'
import { PollStatus, Roles } from 'constants/index'
import { isEqual, sortBy, first } from 'lodash'
import { renderCol } from 'utils/renderColumns'
import { colors } from 'utils/colors'
import talkSlice from 'store/talk'
import moment from 'moment-timezone'
import { ALL_DATES_FILTER } from 'store/event'
import { convertTimestampToLocalString } from 'utils/times'
import TextField from '@material-ui/core/TextField'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { nanoid } from 'nanoid'
import ConfirmModal from 'components/ConfirmationModal'
import { toast } from 'utils/notify'
import DataLoading from 'components/DataLoading'

const app_base_url = window.location.origin

const emptyQuestion = {
  title: '',
  options: [
    { optId: nanoid(), opt: '' },
    { optId: nanoid(), opt: '' }
  ]
}
const pollInitialValues = {
  title: '',
  speakerId: '',
  questions: [emptyQuestion]
}

const PollsListing = () => {
  const dispatch = useDispatch()
  const classes = useStyles()
  const options = getTableOptions()

  const [loading, setLoading] = useState(false)
  const [questionsLoading, setQuestionsLoading] = useState(false)
  const [editedPoll, setEditedPoll] = useState(null)
  const [confirmModal, setConfirmModal] = useState(false)
  const [deletedPollId, setDeletedPollId] = useState('')
  const [isDeletingPoll, setIsDeletingPoll] = useState(false)
  const [resetPollId, setResetPollId] = useState('')
  const [isResettingPoll, setIsResettingPoll] = useState(false)
  const [menuAnchorEl, setMenuAnchorEl] = useState(null)
  const [currentPoll, setCurrentPoll] = useState(null)
  const [pollValues, setPollValues] = useState(pollInitialValues)
  const [dateFilters, setDateFilters] = useState([ALL_DATES_FILTER])
  const { user } = useSelector((state) => state.auth)
  const { speakers } = useSelector((state) => state.speaker)
  const {
    polls,
    confFilter,
    selectedSpeaker,
    selectedDate,
    selectedStatus,
    currentPage,
    pageSize
  } = useSelector((state) => state.polling)

  const [openAddPollModal, setOpenAddPollModal] = useState(false)

  const {
    conferences,
    conference,
    inProgress: confProgress
  } = useSelector((state) => state.conference)
  const activeConferences = conferences?.filter(
    (row) => row.isActive && !row.isArchived && row?.havePolls
  )
  const orderedConferences = caseInsensitiveSort(activeConferences, 'confName')
  const [formattedPolls, setFormattedPolls] = useState(polls || [])

  const handleCloseDeleteModal = () => {
    setDeletedPollId('')
  }

  const handleDeletePoll = async () => {
    setIsDeletingPoll(true)
    const isDeleted = await dispatch(deletePollById(deletedPollId, { validateChecks: true }))
    if (isDeleted) {
      dispatch(pollingSlice.actions.filterDeletedPolls({ pollId: deletedPollId }))
      toast.success('Poll and its questions deleted.')
    }
    setIsDeletingPoll(false)
  }

  const handleCloseResetModal = () => {
    setResetPollId('')
  }

  const handleResetPoll = async () => {
    setIsResettingPoll(true)
    await dispatch(resetPollById(resetPollId))
    toast.success('Poll reset.')
    setIsResettingPoll(false)
    setResetPollId('')
  }

  useEffect(() => {
    if (confFilter && user?.role === Roles.SUPER_ADMIN && !confProgress) {
      conferences.every((conf) => {
        if (conf?.id === confFilter && (conf?.isActive === false || conf?.isArchived === true)) {
          dispatch(pollingSlice.actions.setConferenceFilter(first(orderedConferences)?.id))
          return false
        }
        return true
      })
    } else if (!confFilter && user?.role === Roles.SUPER_ADMIN && !confProgress) {
      dispatch(pollingSlice.actions.setConferenceFilter(first(orderedConferences)?.id))
    }
  }, [confProgress, confFilter])

  useEffect(() => {
    if (user.role === Roles.SUPER_ADMIN && !confFilter && !confProgress) {
      dispatch(pollingSlice.actions.setConferenceFilter(first(orderedConferences)?.id))
    }
  }, [confProgress, confFilter])

  const handleCloseModal = (formValues) => {
    if (isEqual(pollValues, formValues)) {
      setOpenAddPollModal(false)
      setEditedPoll(null)
      setPollValues(pollInitialValues)
    } else {
      setConfirmModal(true)
    }
  }

  const handleCloseConfirmModal = () => {
    setConfirmModal(false)
    setEditedPoll(null)
    setOpenAddPollModal(false)
    setPollValues(pollInitialValues)
  }

  const handleOpenModalForEdit = async (poll) => {
    setQuestionsLoading(true)
    setEditedPoll(poll)
    const questions = await fetchPollQuestions(poll?.id)
    questions.forEach((question) => sortBy(question.options, 'order'))
    setPollValues({
      title: poll?.title,
      questions: questions
    })
    setQuestionsLoading(false)
  }

  const tableHead = [
    {
      name: 'Sr #',
      options: {
        customBodyRender: (value) => <div style={{ minWidth: '90px' }}>{value}</div>
      }
    },
    'Poll Title',
    'Poll Type',
    {
      name: 'Event/Sub Event/Poster/Talk',
      options: {
        sort: false
      }
    },
    {
      name: 'Speaker Name',
      options: {
        sort: false
      }
    },
    'Status',
    {
      name: 'Actions',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => renderCol(value),
        customHeadLabelRender: (meta) => (
          <div style={{ textAlign: 'center' }}>{renderCol(meta.label)}</div>
        )
      }
    }
  ]

  const formatTableData = (polls) =>
    polls?.map((poll) => {
      const conf =
        user?.role !== Roles.SUPER_ADMIN
          ? conference
          : conferences?.find((conf) => conf?.id === confFilter)
      const confTimezone = conf?.timezone
      const conf24HrFormat = conf?.is24hrFormat
      const { status, bgColor, color } = getPollStatusText(poll?.status)
      return [
        poll?.srNo,
        poll?.title,
        getPollType(poll),
        <Box component="span">
          <Typography variant="body2">{poll?.associatedTitle}</Typography>
          {poll?.startDate && (
            <Typography variant="body2" className={classes.subText}>
              {convertTimestampToLocalString(poll?.startDate, confTimezone, conf24HrFormat)} -{' '}
              {convertTimestampToLocalString(poll?.endDate, confTimezone, conf24HrFormat)}
            </Typography>
          )}
        </Box>,
        poll?.speaker ?? 'N/A',
        <Chip label={status} style={{ backgroundColor: bgColor, color, width: '90px' }} />,
        <div style={{ textAlign: 'center' }}>
          <IconButton
            size="small"
            onClick={(event) => {
              setMenuAnchorEl(event.currentTarget)
              setCurrentPoll(poll)
            }}>
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={menuAnchorEl}
            open={Boolean(menuAnchorEl) && currentPoll?.id === poll?.id}
            onClose={() => {
              setMenuAnchorEl(null)
              setCurrentPoll(null)
            }}>
            <MenuItem
              onClick={() => {
                setMenuAnchorEl(null)
                setCurrentPoll(null)
                handleOpenModalForEdit(poll)
              }}>
              <Edit fontSize="small" color="action" style={{ marginRight: 10 }} />
              Edit
            </MenuItem>
            <MenuItem
              onClick={() => {
                setMenuAnchorEl(null)
                setCurrentPoll(null)
                setDeletedPollId(poll?.id)
              }}>
              <Delete fontSize="small" color="action" style={{ marginRight: 10 }} />
              Delete
            </MenuItem>
            {poll?.status === PollStatus.CLOSED && (
              <MenuItem
                onClick={() => {
                  setMenuAnchorEl(null)
                  setCurrentPoll(null)
                  downloadPollResultsInCSV(poll)
                }}>
                <Download fontSize="small" color="action" style={{ marginRight: 10 }} />
                Download Results in CSV
              </MenuItem>
            )}
            {poll?.status !== PollStatus.CREATED && (
              <MenuItem
                onClick={() => {
                  setMenuAnchorEl(null)
                  setCurrentPoll(null)
                  setResetPollId(poll?.id)
                }}>
                <RestartAlt fontSize="small" color="action" style={{ marginRight: 10 }} />
                Reset Poll
              </MenuItem>
            )}
            {poll?.isAssociated && poll?.status !== PollStatus.CLOSED && (
              <MenuItem
                onClick={() => {
                  setMenuAnchorEl(null)
                  setCurrentPoll(null)
                  navigator.clipboard.writeText(`${app_base_url}/polls/${poll?.id}`)
                  toast.info('Poll link copied to clipboard', { duration: 1000, isClosable: true })
                }}>
                <ContentCopy fontSize="small" color="action" style={{ marginRight: 10 }} />
                Copy Poll Link
              </MenuItem>
            )}
          </Menu>
        </div>
      ]
    })

  useEffect(() => {
    setLoading(true)
    const unsub = getSpeakers(
      user?.role === Roles.SUPER_ADMIN && confFilter ? confFilter : user?.conferenceId || '',
      (data) => {
        dispatch(speakerSlice.actions.setSpeakers(caseInsensitiveSort(data, 'fullName')))
      }
    )
    return () => unsub && unsub()
  }, [dispatch, user?.conferenceId, confFilter])

  useEffect(() => {
    setLoading(true)
    getConferenceBasedPolls(
      user?.role === Roles.SUPER_ADMIN && confFilter ? confFilter : user?.conferenceId
    ).then((data) => {
      dispatch(pollingSlice.actions.setPolls(data))
      setLoading(false)
    })
  }, [dispatch, confFilter, user?.conferenceId])

  const reloadPolls = async () => {
    await getConferenceBasedPolls(
      user?.role === Roles.SUPER_ADMIN && confFilter ? confFilter : user?.conferenceId
    ).then((data) => {
      dispatch(pollingSlice.actions.setPolls(data))
    })
  }

  useEffect(() => {
    const confTimezone =
      user?.role !== Roles.SUPER_ADMIN
        ? conference?.timezone
        : conferences?.find((conf) => conf?.id === confFilter)?.timezone
    const filteredPollsByEventOrSessionOrTalkDate =
      selectedDate === ALL_DATES_FILTER
        ? polls
        : polls?.filter(
            (poll) => moment(poll?.startDate).tz(confTimezone).format('M/D/YYYY') === selectedDate
          )

    const filteredPollsByStatus =
      selectedStatus === 'All'
        ? filteredPollsByEventOrSessionOrTalkDate
        : filteredPollsByEventOrSessionOrTalkDate.filter((poll) =>
            [PollStatus.STARTED, PollStatus.RESUMED].includes(selectedStatus)
              ? [PollStatus.STARTED, PollStatus.RESUMED].includes(poll.status)
              : poll.status === selectedStatus
          )

    const filteredPollsBySpeaker = selectedSpeaker?.id
      ? filteredPollsByStatus?.filter((poll) => poll.speakerId === selectedSpeaker?.id)
      : filteredPollsByStatus

    setFormattedPolls(filteredPollsBySpeaker)
  }, [polls, selectedDate, selectedStatus, selectedSpeaker?.id])

  useEffect(() => {
    if (polls?.length) {
      const confTimezone =
        user?.role !== Roles.SUPER_ADMIN
          ? conference?.timezone
          : conferences?.find((conf) => conf?.id === confFilter)?.timezone
      const DateFilters = polls?.reduce((filters, poll) => {
        const date = poll?.startDate
          ? moment(poll?.startDate).tz(confTimezone).format('M/D/YYYY')
          : null
        if (date && !filters?.includes(date)) {
          filters.push(date)
        }
        return filters
      }, [])

      if (!DateFilters.includes(selectedDate)) {
        dispatch(talkSlice.actions.setSelectedDate(ALL_DATES_FILTER))
      }
      setDateFilters([ALL_DATES_FILTER, ...DateFilters])
    }
  }, [polls])

  if (loading) {
    return (
      <Box sx={{ mt: '3rem' }}>
        <DataLoading />
      </Box>
    )
  }

  return (
    <Box>
      <Box className={classes.headBar}>
        <Typography variant="h5" className={classes.headerTitle}>
          Polls
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          {user?.role !== Roles.SUPER_ADMIN && (
            <Button variant="contained" color="primary" onClick={() => setOpenAddPollModal(true)}>
              Add Poll
            </Button>
          )}
          {user?.role === Roles.SUPER_ADMIN && (
            <Box className={classes.actions}>
              <Box className={classes.filterDropdown}>
                <Typography className={classes.filterLabel}>Conference: </Typography>
                {confProgress ? (
                  <CircularProgress size={25} />
                ) : (
                  <FormControl fullWidth variant="outlined" size="small" className={classes.select}>
                    <Select
                      value={confFilter}
                      onChange={(event) => {
                        dispatch(pollingSlice.actions.setConferenceFilter(event.target.value))
                        dispatch(pollingSlice.actions.setSelectedSpeaker('All'))
                        dispatch(pollingSlice.actions.setSelectedDate('All Dates'))
                        dispatch(pollingSlice.actions.setSelectedStatus('All'))
                      }}>
                      {orderedConferences?.map((conf) => (
                        <MenuItem value={conf?.id} key={conf?.id}>
                          {conf?.confName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      <Box className={classes.customCard}>
        <Typography className={classes.cardsInfo}>
          Use fields given below to filter the polls
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Box className={classes.dropdown}>
            <Typography variant="body2" className={classes.dropdownLabel}>
              Event/Sub Event/Poster/Talk Date
            </Typography>
            <FormControl fullWidth variant="outlined" size="medium">
              <Select
                value={selectedDate}
                onChange={(event) => {
                  const value = event.target.value
                  dispatch(pollingSlice.actions.setSelectedDate(value))
                }}>
                {dateFilters?.map((filter) => (
                  <MenuItem value={filter} key={filter}>
                    {filter}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box className={classes.dropdown}>
            <Typography variant="body2" className={classes.dropdownLabel}>
              Status
            </Typography>
            <FormControl fullWidth variant="outlined" size="medium">
              <Select
                value={selectedStatus}
                onChange={(event) =>
                  dispatch(pollingSlice.actions.setSelectedStatus(event.target.value))
                }>
                <MenuItem value="All">All</MenuItem>
                {Object.values(PollStatus)?.map(
                  (status) =>
                    status !== 'resumed' && (
                      <MenuItem value={status} key={status}>
                        {getPollStatusText(status).status}
                      </MenuItem>
                    )
                )}
              </Select>
            </FormControl>
          </Box>
          <Box className={classes.dropdown}>
            <Typography variant="body2" className={classes.dropdownLabel}>
              Speaker
            </Typography>

            <Autocomplete
              variant="outlined"
              options={speakers}
              onChange={(e, value) => {
                dispatch(pollingSlice.actions.setSelectedSpeaker(value))
              }}
              getOptionLabel={(option) => option?.fullName}
              value={selectedSpeaker}
              renderOption={(option) => <li className={classes.listItem}>{option.fullName}</li>}
              renderInput={(params) => (
                <TextField {...params} placeholder="Select speaker" variant="outlined" />
              )}
            />
          </Box>
        </Box>
      </Box>

      <Wrapper inProgress={loading}>
        <Paper variant="outlined" style={{ marginTop: '24px' }}>
          {formattedPolls?.length ? (
            <BasicTable
              data={formatTableData(formattedPolls)}
              columns={tableHead}
              options={{
                ...options,
                onChangePage: (page) => dispatch(pollingSlice.actions.setCurrentPage(page)),
                onChangeRowsPerPage: (size) => dispatch(pollingSlice.actions.setPageSize(size)),
                page: currentPage,
                rowsPerPage: pageSize
              }}
            />
          ) : (
            <ResourceNotFound
              message={
                selectedSpeaker === 'All'
                  ? `No polls found for this conference?.`
                  : 'No polls found for this speaker.'
              }
            />
          )}
        </Paper>
      </Wrapper>

      <Dialog
        open={openAddPollModal}
        maxWidth={false}
        PaperProps={{ className: classes.pollDialogPaper }}>
        <DialogContent className={classes.pollModalContent}>
          <PollingForm
            forApproval={true}
            editedPollId={null}
            pollValues={pollValues}
            speakerId={null}
            conferenceId={
              user?.role === Roles.SUPER_ADMIN && confFilter ? confFilter : user?.conferenceId
            }
            associateSpeaker={true}
            subjectId={null}
            handleCloseModal={handleCloseModal}
            handleCloseConfirmModal={() => setOpenAddPollModal(false)}
            reloadPolls={reloadPolls}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(editedPoll?.id)}
        maxWidth={false}
        PaperProps={{ className: classes.pollDialogPaper }}>
        <DialogContent className={classes.pollModalContent}>
          {questionsLoading ? (
            <Box className={classes.loader}>
              <CircularProgress />
            </Box>
          ) : (
            <PollingForm
              forApproval={true}
              editedPollId={editedPoll?.id}
              pollValues={pollValues}
              pollStatus={editedPoll?.status}
              speakerId={editedPoll?.speakerId}
              conferenceId={
                user?.role === Roles.SUPER_ADMIN && confFilter ? confFilter : user?.conferenceId
              }
              handleCloseModal={handleCloseModal}
              handleCloseConfirmModal={handleCloseConfirmModal}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog fullWidth maxWidth="sm" open={confirmModal} onClose={() => setConfirmModal(false)}>
        <Box className={classes.warningModalHeader}>
          <Warning className={classes.warningIcon} />
          <Typography className={classes.warningTitle}>Warning</Typography>
        </Box>
        <DialogContent className={classes.modalContent}>
          <Typography variant="body2">
            Any unsaved data will be lost. Press CANCEL to go back and save these changes, or OK to
            discard these changes.
          </Typography>

          <Box className={classes.actions}>
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={handleCloseConfirmModal}>
              ok
            </Button>
            <Button size="medium" onClick={() => setConfirmModal(false)}>
              cancel
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
      <ConfirmModal
        dialogTitle="Delete Poll"
        dialogText="You're about to delete this poll, which may have responses to the questions. All existing answers will be lost. Are you sure you want to continue?"
        open={!!deletedPollId}
        closeModal={handleCloseDeleteModal}
        handleConfirm={handleDeletePoll}
        inProgress={isDeletingPoll}
      />
      <ConfirmModal
        dialogTitle="Reset poll"
        dialogText="You're about to reset this poll, which may have responses to the questions. All existing answers will be lost. Are you sure you want to continue?"
        open={!!resetPollId}
        closeModal={handleCloseResetModal}
        handleConfirm={handleResetPoll}
        inProgress={isResettingPoll}
      />
    </Box>
  )
}

export default PollsListing

const useStyles = makeStyles({
  listItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem'
  },
  headerTitle: {
    fontWeight: 600,
    color: '#263238'
  },
  headBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  select: {
    minWidth: '200px'
  },
  filterDropdown: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    justifyContent: 'flex-end'
  },
  filterLabel: {
    width: '100%',
    fontSize: '1rem',
    textAlign: 'right',
    marginRight: '8px',
    fontWeight: 'bold'
  },
  pollDialogPaper: {
    width: '92vw',
    maxWidth: '1400px',
    height: '92vh',
    maxHeight: '92vh'
  },
  pollModalContent: {
    overflowX: 'hidden',
    overflowY: 'hidden',
    padding: '0px 30px 30px !important'
  },
  warningModalHeader: {
    padding: '20px 20px 10px 20px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  warningIcon: {
    color: '#ffa726'
  },
  warningTitle: {
    fontSize: '16px',
    fontWeight: 'bold'
  },
  modalContent: {
    padding: '4px 16px 4px 22px'
  },
  actions: {
    marginTop: '20px',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    marginBottom: '10px'
  },
  loader: {
    margin: '16px 0px',
    height: '100px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  subText: {
    marginTop: '4px',
    fontSize: '12px',
    color: '#757575'
  },
  customCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    padding: '1.25rem',
    border: '1px solid #bdbdbd',
    borderRadius: '12px',
    marginTop: '16px'
  },
  cardsInfo: {
    color: colors.indigo,
    fontSize: '16px',
    fontWeight: 500
  },
  dropdown: {
    width: '320px',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.2rem'
  },
  dropdownLabel: {
    fontSize: '12px',
    color: '#616161',
    whiteSpace: 'nowrap'
  }
})
